/**
 * WF-CLI-01 and WF-CLI-02's vocabulary, shared by the screens and the API.
 *
 * **These four constants must be identical on both sides or the product lies to
 * a person.** The screen disables `Créer mon compte` on a trivial PIN; the API
 * refuses one. If the two lists ever differ, a buyer either sees a control
 * enabled that the server will refuse , which is a form that fails after it was
 * filled , or a control disabled for a code the server would have accepted,
 * which is a customer turned away for nothing. Neither failure announces
 * itself, and both are invisible to a test that stubs the other side.
 *
 * So they live here, imported by `identity/domain` and by `client-web`, and
 * neither writes its own copy. That is `domain-types`' stated job (9.1: *"shared
 * enums and label maps, one vocabulary per concept"*) and the reason round 176
 * put the expertise verdict here and round 177 the bank stages.
 */

/** WF-CLI-01, step 4: the four cities, a closed list. */
export const VILLES_INSCRIPTION: readonly string[] = ["Bamako", "Ségou", "Sikasso", "Koutiala"];

/**
 * WF-CLI-01's trivial list, transcribed, and it is a LIST PLUS A RULE.
 *
 * The workflow names `1234`, `4321`, `1111`, `0000`, `1212` **and any four
 * identical digits**. Both are kept: the rule alone would miss `1234`, and the
 * list alone would miss `7777`.
 */
export const PINS_TRIVIAUX: readonly string[] = ["1234", "4321", "1111", "0000", "1212"];

export const estPinTrivial = (pin: string): boolean =>
  PINS_TRIVIAUX.includes(pin) || /^(\d)\1{3}$/u.test(pin);

/** The exact sentence WF-CLI-01 shows. */
export const PIN_TRIVIAL_MESSAGE =
  "Choisissez un code moins facile à deviner. Évitez les chiffres identiques ou qui se suivent.";

/** The exact sentence WF-CLI-02 shows at the third failure. */
export const PIN_LOCKED_MESSAGE =
  "Trop de tentatives. Pour votre sécurité, nous devons vérifier votre numéro par SMS avant de vous laisser choisir un nouveau code.";

/** WF-CLI-01: at least eight digits after stripping spaces. */
export const PHONE_MIN_DIGITS = 8;

export const normalisePhone = (raw: string): string => raw.replaceAll(/[\s.-]/gu, "");

/** WF-CLI-01, step 2: the prefix is fixed and the field is the rest. */
export const PREFIXE_MALI = "+223";

/**
 * WF-CLI-02, after each wrong attempt , **with its agreement COMPUTED, and that
 * is a recorded deviation from the workflow's literal text.**
 *
 * WF-CLI-02 writes the sentence as *"`Code incorrect. Il vous reste N
 * essai(s).`"*, with the plural in parentheses. Section 06, **6.5.5** forbids
 * exactly that: *agreement is computed, never parenthesised*, and the repository
 * enforces it with a lint rule that refuses the parenthesised form anywhere. The
 * two cannot both be honoured, and 06 is the cross-cutting law while WF-CLI-02
 * is a mockup transcription , so the agreement is computed here.
 *
 * The plural starts at TWO in French, which an English-trained pluraliser gets
 * wrong: `1 essai`, `2 essais`. `packages/i18n`'s `pl` knows this, and this file
 * cannot import it , `domain-types` may not read `i18n` (9.3.1) , so the rule is
 * written out rather than reached for.
 */
export const phraseEssaisRestants = (restants: number): string =>
  `Code incorrect. Il vous reste ${String(restants)} ${Math.abs(restants) >= 2 ? "essais" : "essai"}.`;

/** WF-CLI-02: the lockout fires at three failures, server-side and per device. */
export const PIN_ESSAIS_AVANT_VERROU = 3;

/**
 * `P0-IDN-045`: **the handset's fingerprint, and it is a HASH before it leaves
 * the browser.**
 *
 * The staging sitting of 29 August 2026 died at the last door. `useSession.ts`
 * built the fingerprint as `` `${navigator.userAgent}|${navigator.language}` ``
 * , around 117 characters on an ordinary Chrome , and both columns that store
 * it are `VARCHAR(64)`. `pin/login` is the only route that sends one, so a
 * registration could complete and a login could not: `500`, `Une erreur interne
 * est survenue.`, and no line in any log.
 *
 * **Hashing is the fix rather than a wider column, and the reasons are three.**
 * The digest is exactly 64 characters, forever, whatever a future browser puts
 * in its user agent. It needs no migration. And it stops a raw user agent from
 * being stored at all , the value the server keeps is now an opaque device
 * identity and not a description of the person's machine, which is the smaller
 * thing to hold. If a human-readable label is ever wanted, 05 already has
 * `device_label` for it and it becomes its own deliberately minimized field by
 * order, never a side effect of this one.
 *
 * **The cost, stated:** every fingerprint already stored changes value once, so
 * a handset that was "recognised" must be re-verified through the OTP path. On
 * staging that is demonstration data; nothing else has ever run this route.
 *
 * **Web Crypto, so this file stays dependency-free**, and asynchronous because
 * `crypto.subtle.digest` is. The caller awaits it; there is no synchronous
 * digest in a browser that does not cost a dependency, and a dependency for
 * sixty-four characters is the wrong trade.
 */
export const EMPREINTE_LONGUEUR = 64;

/** What the client hashes: the same expression it always built, unchanged. */
export const empreinteBrute = (userAgent: string, language: string): string => `${userAgent}|${language}`;

/**
 * The sha256 hex of that expression , exactly `EMPREINTE_LONGUEUR` characters.
 *
 * **Every spec that sends a fingerprint sends THIS**, per the fixture law of
 * round 183: what a spec sends is what the client sends. `"device-a"` was eight
 * characters and green over a defect the browser met on its first attempt.
 */
export async function empreinteAppareil(userAgent: string, language: string): Promise<string> {
  const octets = new TextEncoder().encode(empreinteBrute(userAgent, language));
  const digest = await globalThis.crypto.subtle.digest("SHA-256", octets);
  return [...new Uint8Array(digest)].map((byte) => byte.toString(16).padStart(2, "0")).join("");
}
